// src/services/UserRolesService.js
import {
  collection,
  doc,
  getDoc,
  setDoc,
  query,
  where,
  getDocs,
  updateDoc,
  serverTimestamp,
  orderBy,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

const USERS_COLLECTION = 'users';
const APPLICATIONS_COLLECTION = 'serviceProviderApplications';

// User role constants
export const USER_ROLES = {
  ADMIN: 'admin',
  SERVICE_PROVIDER: 'service_provider',
  USER: 'user'
};

// Application status constants
export const APPLICATION_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected'
};

// Stripe Connect onboarding status
export const STRIPE_ONBOARDING_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed'
};

// Default platform fee percentage (20%)
export const DEFAULT_PLATFORM_FEE_PERCENTAGE = 20;

/**
 * Get a user's role information
 */
export const getUserRole = async (userId) => {
  if (!userId) {
      console.error("getUserRole called with null or undefined userId");
      return { role: USER_ROLES.USER, status: null };
  }
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      // Ensure platformFeePercentage exists, default to 20%
      if (userData.platformFeePercentage === undefined) {
        userData.platformFeePercentage = DEFAULT_PLATFORM_FEE_PERCENTAGE;
      }
      return userData;
    } else {
      console.warn(`User document for ${userId} not found. Returning default role.`);
      return { role: USER_ROLES.USER, status: null, platformFeePercentage: DEFAULT_PLATFORM_FEE_PERCENTAGE };
    }
  } catch (error) {
    console.error(`Error getting user role for ${userId}:`, error);
    return { role: USER_ROLES.USER, status: null, platformFeePercentage: DEFAULT_PLATFORM_FEE_PERCENTAGE };
  }
};

/**
 * Update user's platform fee percentage
 */
export const updateUserPlatformFee = async (userId, platformFeePercentage) => {
  if (!userId || platformFeePercentage === undefined) {
    throw new Error("User ID and platform fee percentage are required");
  }
  
  // Validate fee percentage (1-20%)
  if (platformFeePercentage < 1 || platformFeePercentage > 20) {
    throw new Error("Platform fee percentage must be between 1% and 20%");
  }
  
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userRef, {
      platformFeePercentage: platformFeePercentage,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error("Error updating platform fee:", error);
    throw error;
  }
};

/**
 * Update user's Stripe Connect account ID
 */
export const updateUserStripeAccount = async (userId, stripeAccountId, onboardingStatus = STRIPE_ONBOARDING_STATUS.COMPLETED) => {
  if (!userId || !stripeAccountId) {
    throw new Error("User ID and Stripe Account ID are required");
  }
  
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userRef, {
      stripeConnectAccountId: stripeAccountId,
      stripeOnboardingStatus: onboardingStatus,
      stripeOnboardingCompletedAt: onboardingStatus === STRIPE_ONBOARDING_STATUS.COMPLETED ? serverTimestamp() : null,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error("Error updating user Stripe account:", error);
    throw error;
  }
};

/**
 * Get user's Stripe Connect account ID
 */
export const getUserStripeAccount = async (userId) => {
  if (!userId) return null;
  
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const data = userDoc.data();
      return {
        stripeAccountId: data.stripeConnectAccountId || null,
        onboardingStatus: data.stripeOnboardingStatus || STRIPE_ONBOARDING_STATUS.NOT_STARTED,
        platformFeePercentage: data.platformFeePercentage || DEFAULT_PLATFORM_FEE_PERCENTAGE
      };
    }
    
    return null;
  } catch (error) {
    console.error("Error getting user Stripe account:", error);
    return null;
  }
};

/**
 * Check if a user is an admin
 */
export const isUserAdmin = async (userId) => {
   if (!userId) return false;
  try {
    const userRole = await getUserRole(userId);
    return userRole.role === USER_ROLES.ADMIN;
  } catch (error) {
    console.error("Error checking admin status:", error);
    return false;
  }
};

/**
 * Check if a user is an approved service provider
 */
export const isApprovedServiceProvider = async (userId) => {
  if (!userId) return false;
  try {
    const userRole = await getUserRole(userId);

    return (
      (userRole.role === USER_ROLES.SERVICE_PROVIDER || userRole.role === USER_ROLES.ADMIN) &&
      userRole.status === APPLICATION_STATUS.APPROVED
    );
  } catch (error) {
    console.error("Error checking service provider status:", error);
    return false;
  }
};

/**
 * Get application status for a user
 */
export const getApplicationStatus = async (userId) => {
  if (!userId) {
      console.error("getApplicationStatus called with null or undefined userId");
      return null;
  }
  try {
    const appRef = doc(db, APPLICATIONS_COLLECTION, userId);
    const appDoc = await getDoc(appRef);

    if (appDoc.exists()) {
      const data = appDoc.data();
      const toDateOrNull = (timestamp) => timestamp && typeof timestamp.toDate === 'function' ? timestamp.toDate() : null;

      return {
          ...data,
          submittedAt: toDateOrNull(data.submittedAt),
          updatedAt: toDateOrNull(data.updatedAt),
          reviewedAt: toDateOrNull(data.reviewedAt)
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error(`Error getting application status for ${userId}:`, error);
    throw error;
  }
};

/**
 * Submit an application to become a service provider
 */
export const submitProviderApplication = async (userId, applicationData) => {
  if (!userId) {
       throw new Error("Cannot submit application without a user ID.");
   }
  try {
    const { listing, ...providerData } = applicationData;
    const appRef = doc(db, APPLICATIONS_COLLECTION, userId);

    if (listing) {
      // One atomic submit: provider application (pending) + listing (pending),
      // linked by listingId / providerId. Both flip live on a single approval.
      const batch = writeBatch(db);
      const listingRef = doc(collection(db, 'listings'));

      batch.set(listingRef, {
        ...listing,
        userId,
        providerId: userId,
        status: 'pending',
        suppressNotifications: true, // no "listing created" email until it's actually live
        archived: false,
        archivedAt: null,
        currentAttendees: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      batch.set(appRef, {
        ...providerData,
        userId,
        listingId: listingRef.id,
        status: APPLICATION_STATUS.PENDING,
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      await batch.commit();
      return true;
    }

    const application = {
      ...applicationData,
      userId,
      status: APPLICATION_STATUS.PENDING,
      submittedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(appRef, application);
    return true;
  } catch (error) {
    console.error("Error submitting application:", error);
    throw error;
  }
};

/**
 * Get all pending service provider applications
 */
export const getPendingApplications = async () => {
  try {
    const q = query(
      collection(db, APPLICATIONS_COLLECTION),
      where("status", "==", APPLICATION_STATUS.PENDING),
      orderBy("submittedAt", "asc")
    );

    const querySnapshot = await getDocs(q);
    const toDateOrNull = (timestamp) => timestamp && typeof timestamp.toDate === 'function' ? timestamp.toDate() : null;

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      submittedAt: toDateOrNull(doc.data().submittedAt),
      updatedAt: toDateOrNull(doc.data().updatedAt)
    }));
  } catch (error) {
    console.error("Error getting pending applications:", error);
    throw error;
  }
};

/**
 * Approve a service provider application
 */
export const approveApplication = async (userId) => {
  if (!userId) throw new Error("Cannot approve application without a user ID.");
  try {
    const appRef = doc(db, APPLICATIONS_COLLECTION, userId);
    const appDoc = await getDoc(appRef);
    const appData = appDoc.exists() ? appDoc.data() : null;

    const userRef = doc(db, USERS_COLLECTION, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
        console.error(`User document ${userId} does not exist. Cannot approve.`);
        throw new Error(`User document ${userId} not found.`);
    }

    const userData = userDoc.data();

    // One approval, one atomic commit: provider goes active and the listing
    // goes live together (or neither does).
    const batch = writeBatch(db);

    const appUpdate = {
      status: APPLICATION_STATUS.APPROVED,
      updatedAt: serverTimestamp(),
      reviewedAt: serverTimestamp()
    };

    const userUpdate = {
      status: APPLICATION_STATUS.APPROVED,
      stripeOnboardingStatus: STRIPE_ONBOARDING_STATUS.NOT_STARTED, // Initialize Stripe onboarding status
      platformFeePercentage: userData.platformFeePercentage || DEFAULT_PLATFORM_FEE_PERCENTAGE, // Set default platform fee
      updatedAt: serverTimestamp()
    };

    if (userData.role !== USER_ROLES.ADMIN) {
      userUpdate.role = USER_ROLES.SERVICE_PROVIDER;
    }

    batch.update(userRef, userUpdate);

    if (appData?.listingId) {
      // Current flow: the pending listing was created at submit — flip it live
      batch.update(doc(db, 'listings', appData.listingId), {
        status: 'published',
        publishedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } else if (appData?.listing && !appData.publishedListingId) {
      // Legacy applications stored the listing inline: publish it now.
      // publishedListingId guards against duplicates if approval is re-run.
      const listingRef = doc(collection(db, 'listings'));
      batch.set(listingRef, {
        ...appData.listing,
        userId,
        providerId: userId,
        status: 'published',
        archived: false,
        archivedAt: null,
        currentAttendees: 0,
        publishedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      appUpdate.publishedListingId = listingRef.id;
    }

    batch.update(appRef, appUpdate);

    await batch.commit();

    return true;
  } catch (error) {
    console.error("Error approving application:", error);
    throw error;
  }
};

/**
 * Reject a service provider application
 */
export const rejectApplication = async (userId, rejectionReason) => {
 if (!userId) throw new Error("Cannot reject application without a user ID.");
  try {
    const appRef = doc(db, APPLICATIONS_COLLECTION, userId);
    const appDoc = await getDoc(appRef);
    const appData = appDoc.exists() ? appDoc.data() : null;

    const userRef = doc(db, USERS_COLLECTION, userId);
    const userDoc = await getDoc(userRef);

    // Reject application, user status, and the pending listing together
    const batch = writeBatch(db);

    batch.update(appRef, {
      status: APPLICATION_STATUS.REJECTED,
      rejectionReason: rejectionReason || "Application does not meet requirements.",
      updatedAt: serverTimestamp(),
      reviewedAt: serverTimestamp()
    });

    if (userDoc.exists()) {
      const userData = userDoc.data();

      const updateData = {
          status: APPLICATION_STATUS.REJECTED,
          updatedAt: serverTimestamp()
      };

      if (userData.role !== USER_ROLES.ADMIN) {
          updateData.role = USER_ROLES.USER;
      }

      batch.update(userRef, updateData);
    } else {
      console.warn(`User document ${userId} does not exist. Cannot update status on rejection.`);
    }

    if (appData?.listingId) {
      batch.update(doc(db, 'listings', appData.listingId), {
        status: 'rejected',
        updatedAt: serverTimestamp()
      });
    }

    await batch.commit();

    return true;
  } catch (error) {
    console.error("Error rejecting application:", error);
    throw error;
  }
};

/**
 * Create an admin user (use with caution - requires secure setup)
 */
export const createAdminUser = async (userId) => {
  if (!userId) throw new Error("Cannot create admin without a user ID.");
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    await setDoc(userRef, {
      role: USER_ROLES.ADMIN,
      status: APPLICATION_STATUS.APPROVED,
      platformFeePercentage: DEFAULT_PLATFORM_FEE_PERCENTAGE,
      updatedAt: serverTimestamp()
    }, { merge: true });

    return true;
  } catch (error) {
    console.error("Error creating admin user:", error);
    throw error;
  }
};